import { useEffect, useRef, useState } from "react";
import BillingViewComponent from "./BillingViewComponent";
import ReminderComponent from "./ReminderComponent";
import BillingSummaryComponent, { IMirrorBillingSummary } from "./BillingSummaryComponent";
import VeticScreenSaver from "./VeticScreenSaver";
import MirrorFollowUpComponent from "./MirrorFollowUpComponent";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import type { EmrMirrorCommand, MembershipMirrorCommand, PaymentMirrorCommand, PitchingMirrorCommand } from "./types";
import { useSocket } from "@/app/components/socket/SocketProvider";
import { setReminders } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useFetchMirrorStateAPI, IMirrorStateResponseData } from "@/app/hooks/api_hooks/Cart/useFetchMirrorStateAPI";
import { useSendBookingMirrorEventAPI } from "@/app/hooks/api_hooks/bookings/useSendBookingMirrorEventAPI";
import { VMS_CLICK_EVENTS, VMS_SCREEN_IDS, useVMSClickLog } from "@/app/hooks/useVMSClickLog";

export type MirrorStatus = "ACTIVE" | "IDLE" | "CHECKED_OUT" | "LOGGED_OUT";

const BillingMirrorComponent = ({
  embedded = false,
  compact,
  cartIdOverride,
  customerIdOverride,
  source,
  emrCommand,
  membershipCommand,
  pitchingCommand
}: {
  embedded?: boolean;
  compact?: boolean;
  cartIdOverride?: string | null;
  customerIdOverride?: string | null;
  source?: string;
  emrCommand?: EmrMirrorCommand | null;
  membershipCommand?: MembershipMirrorCommand | null
  pitchingCommand?: PitchingMirrorCommand | null
}) => {
  const [billingData, setBillingData] = useState<any | null>(null);
  const mappedClinicId = useAppSelector((state) => state.root.UIStates.mappedClinicId);
  const mappedClinicName = useAppSelector((state => state.root.UIStates.mappedClinicName))
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const { execute: logAPI } = useClickLogAPI();
  const { logVMSClick } = useVMSClickLog(VMS_SCREEN_IDS.BILLING_MIRROR);
  const [cartId, setCartId] = useState<string | null>(null);
  const { sendJsonMessage, readyState, lastJsonMessage: socketJsonMessage, lastMessage: socketRawMessage } = useSocket();
  const [shouldShowReminders, setShouldShowReminders] = useState(false);
  const [billingSummary, setBillingSummary] = useState<IMirrorBillingSummary | null>(null);
  const lastLoggedMirrorMessageRef = useRef<any>(null);
  const [showMirrorFollowUp, setShowMirrorFollowUp] = useState(false);
  const [followUpCustomerId, setFollowUpCustomerId] = useState<string | null>(null);
  const [followUpVmsUserId, setFollowUpVmsUserId] = useState<string | null>(null);
  const [followUpRecommendations, setFollowUpRecommendations] = useState<any[] | null>(null);
  const [followUpSlotsFilter, setFollowUpSlotsFilter] = useState<any | null>(null);
  const [followUpSelectedSlotId, setFollowUpSelectedSlotId] = useState<string | null>(null);
  const [followUpConfirmedBookingId, setFollowUpConfirmedBookingId] = useState<string | null>(null);
  const [followUpActivePetId, setFollowUpActivePetId] = useState<string | null>(null);
  const [followUpScrollCommand, setFollowUpScrollCommand] = useState<{ direction: "up" | "down"; delta: number; ts: number } | null>(null);
  const [socketEmrCommand, setSocketEmrCommand] = useState<EmrMirrorCommand | null>(null);
  const [paymentCommand, setPaymentCommand] = useState<PaymentMirrorCommand | null>(null);
  const isEmbedded = embedded || source === "ACTION";
  const dispatch = useAppDispatch();
  const { execute: fetchMirrorState } = useFetchMirrorStateAPI();
  const { execute: sendMirrorEvent } = useSendBookingMirrorEventAPI();
  const [billingSummaryCustomerId, setBillingSummaryCustomerId] = useState<string | null>(null);

  // Unconditional reset (no diffing against previous state) so re-presenting the same cart still repaints —
  // replaces the old Firestore clear-then-write trick.
  const applyMirrorState = (data: IMirrorStateResponseData | null | undefined) => {
    const cart_id = data?.cart_id ?? null;
    if (data?.status === "CHECKED_OUT" && !cart_id) return;
    setBillingData(data ?? null);
    setCartId(cart_id);
    if (cart_id) setPaymentCommand((prev) => (prev?.action === "QR_SHOW" && cart_id === cartId ? prev : null));
    localStorage.setItem("Mirrorcartid", cart_id ?? "null");
  };

  useEffect(() => {
    if (isEmbedded) return;
    if (readyState !== WebSocket.OPEN) return;
    const send = (et: string) => {
      sendJsonMessage({ event_type: et });
    };
    send("MIRROR_ALIVE");
    const hb = setInterval(() => {
      send("MIRROR_ALIVE");
    }, 10000);
    const onUnload = () => {
      send("MIRROR_STOP");
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      clearInterval(hb);
      window.removeEventListener("beforeunload", onUnload);
      send("MIRROR_STOP");
    };
  }, [isEmbedded, readyState, sendJsonMessage]);

  useEffect(() => {
    if (cartIdOverride !== undefined) {
      setCartId(cartIdOverride ?? null);
      setBillingData(customerIdOverride ? { customer_id: customerIdOverride } : null);
      return;
    }
    if (!userLogInfo) return;
    // Initial fetch + re-sync on every WS (re)connect. Redis cache returns authoritative state.
    // readyState dep replaces onSnapshot's auto-resync — pub/sub is at-most-once.
    fetchMirrorState().then(applyMirrorState);
    return () => {
      setCartId(null);
      setBillingData(null);
    };
  }, [userLogInfo, cartIdOverride, customerIdOverride, readyState]);

  useEffect(() => {
    if (isEmbedded) return;
    const enterFullscreen = () => {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if ((elem as any).webkitRequestFullscreen) {
        /* Safari */
        (elem as any).webkitRequestFullscreen();
      } else if ((elem as any).msRequestFullscreen) {
        /* IE11 */
        (elem as any).msRequestFullscreen();
      }
    };
    enterFullscreen();
  }, [isEmbedded]);

  useEffect(() => {
    if (isEmbedded) return;
    const logBody: IClickLogBody = {
      event_type: "MIRROR_SCRENN_ACTIVE",
      scr_id: "mirror-view",
      clinic_id: mappedClinicId,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };
    logAPI(logBody);
  }, [isEmbedded]);

  const shouldCompact = compact ?? isEmbedded;
  const appliedClass = shouldCompact
    ? "text-[11px] leading-tight [&_*]:text-[11px] [&_*]:leading-tight"
    : "";
  const resolvedEmrCommand = emrCommand ?? (isEmbedded ? null : socketEmrCommand);

  const handleMirrorActionCommandMessage = (message: any) => {
    if (!message || typeof message !== "object") return false;
    const payload = "payload" in message ? message.payload : message;
    if (!payload || typeof payload !== "object") return false;
    const action = "action" in payload ? (payload as any).action : null;
    if (!action) return false;
    const cartIdFromPayload = (payload as any).cart_id ?? (payload as any).cartId ?? null;
    const actionId =
      ("action_id" in payload && (payload as any).action_id) ||
      ("actionId" in payload && (payload as any).actionId) ||
      ("emr_id" in payload && (payload as any).emr_id) ||
      ("emrId" in payload && (payload as any).emrId) ||
      null;
    if (action === "EMR_OPEN") {
      if (!actionId) return true;
      setSocketEmrCommand({
        action: "OPEN",
        emrId: String(actionId),
        cartId: cartIdFromPayload ? String(cartIdFromPayload) : null,
        ts: Date.now(),
      });
      return true;
    }
    if (action === "EMR_CLOSE") {
      setSocketEmrCommand({
        action: "CLOSE",
        cartId: cartIdFromPayload ? String(cartIdFromPayload) : null,
        ts: Date.now(),
      });
      return true;
    }
    return false;
  };

  const handleMirrorStateMessage = (message: any) => {
    if (!message || typeof message !== "object") return false;
    if (message.event_type !== "mirror_state_update") return false;
    if (message.cart_id) {
      setShowMirrorFollowUp(false);
      setPaymentCommand(null);
      setBillingSummary(null);
    }
    applyMirrorState(message);
    return true;
  };

  const handlePaymentMessage = (message: any) => {
    if (message?.event_type !== "payment_qr") return false;
    const { action, order_id, amount, image_base64, overdue_amount, advance_amount, billing_summary, expires_in } = message;
    if (action && !isEmbedded) {
      logVMSClick(VMS_CLICK_EVENTS.MIRROR_QR_EVENT_RECEIVED, action, {
        event_action: action,
        cart_id: cartId,
        order_id: order_id != null ? String(order_id) : undefined,
        amount: amount != null ? Number(amount) : undefined,
      });
    }
    if (billing_summary) setBillingSummary(billing_summary);
    if (action === "QR_SHOW" || action === "PAYMENT_SUCCESS") {
      setPaymentCommand({
        action,
        orderId: String(order_id ?? ""),
        amount: Number(amount ?? 0),
        qrBase64: image_base64,
        overdueAmount: Number(overdue_amount ?? 0),
        advanceAmount: Number(advance_amount ?? 0),
        expiresIn: expires_in != null ? Number(expires_in) : undefined,
      });
    } else {
      setPaymentCommand((prev) => prev && { ...prev, action: "QR_INACTIVE" });
    }
    return true;
  };

  useEffect(() => {
    if (handleMirrorStateMessage(socketJsonMessage)) {
      return;
    }
    if (handleMirrorActionCommandMessage(socketJsonMessage)) {
      return;
    }
    if (handleReminderMessage(socketJsonMessage)) {
      return;
    }
    if (handlePaymentMessage(socketJsonMessage)) {
      return;
    }
    const data = socketRawMessage?.data;
    if (!data) return;
    const parseText = (rawText: string) => {
      const raw = rawText.trim();
      if (!raw || raw === "pong") return;
      try {
        const parsed = JSON.parse(raw);
        if (handleMirrorStateMessage(parsed)) return;
        if (handleMirrorActionCommandMessage(parsed)) return;
        if (handleReminderMessage(parsed)) return;
        if (handlePaymentMessage(parsed)) return;
      } catch (error) {
        console.warn("[BillingMirror] Failed to parse reminder message:", error);
        return;
      }
    };
    if (typeof data === "string") {
      parseText(data);
      return;
    }
    if (data instanceof Blob) {
      data.text().then(parseText).catch((error) => {
        console.error("[EMRMessage] Failed to parse blob:", error);
      });
      return;
    }
    if (data instanceof ArrayBuffer) {
      parseText(new TextDecoder().decode(data));
      return;
    }
    if (ArrayBuffer.isView(data)) {
      parseText(new TextDecoder().decode(data));
    }
  }, [socketJsonMessage, socketRawMessage]);

  const handleReminderMessage = (message: any) => {
    if (!message || typeof message !== "object") return false;
    const eventType =
      message.event ??
      message.type ??
      message.event_type ??
      null;
    if (!eventType) return false;
    const event = String(eventType).toLowerCase();
    const payload =
      "payload" in message && message.payload && typeof message.payload === "object"
        ? message.payload
        : null;
    const logMirrorView = (event_type: string, extra: Partial<IClickLogBody> = {}) => {
      if (isEmbedded || lastLoggedMirrorMessageRef.current === message) return;
      lastLoggedMirrorMessageRef.current = message;
      logAPI({
        event_type,
        scr_id: "mirror-view",
        clinic_id: mappedClinicId,
        clinic_name: mappedClinicName,
        vms_user_id: userLogInfo,
        timestamp: Date.now(),
        ...extra,
      });
    };
    if (event === "reminders") {
      const voucherGroups = Array.isArray(message.voucher_groups)
        ? message.voucher_groups.filter((voucherGroup: any) => voucherGroup?.group)
        : [];
      logMirrorView("MIRROR_REMINDERS_VIEWED", {
        client_id: message.customer_id ?? payload?.customer_id,
        items_count: voucherGroups.length,
        membership_details: voucherGroups,
      });
      setShowMirrorFollowUp(false);
      setPaymentCommand(null);
      setBillingSummary(null);
      setCartId(null);
      setShouldShowReminders(true);
      dispatch(setReminders(message));
      return true;
    }
    if (event === "billing_summary") {
      logMirrorView("MIRROR_BILLING_SUMMARY_VIEWED", {
        client_id: message.customer_id,
        items_count: message.billing_summary?.line_items_count,
        search_result: message.billing_summary,
      });
      setShowMirrorFollowUp(false);
      setShouldShowReminders(false);
      setPaymentCommand(null);
      setBillingSummary(message.billing_summary);
      setBillingSummaryCustomerId(message.customer_id ?? null);
      return true;
    }
    if (event === "followup_opened") {
      if (!payload) return false;
      const followUpRecommendations =
        payload.recommendation ??
        payload.cm_booking_recommendations ??
        payload.cmBookingRecommendations ??
        null;
      setShowMirrorFollowUp(true);
      setFollowUpCustomerId(payload.customer_id ?? null);
      setFollowUpVmsUserId(payload.vms_user_id ?? null);
      setFollowUpRecommendations(Array.isArray(followUpRecommendations) ? followUpRecommendations : null);
      setFollowUpSelectedSlotId(null);
      setFollowUpConfirmedBookingId(null);
      setFollowUpActivePetId(null);
      return true;
    }
    if (event === "followup_fetch_slots") {
      if (!payload) return false;
      setShowMirrorFollowUp(true);
      setFollowUpSlotsFilter({
        ...payload,
        location_details: payload.location_details ? { ...payload.location_details } : payload.location_details,
      });
      setFollowUpSelectedSlotId(null);
      setFollowUpConfirmedBookingId(null);
      return true;
    }
    if (event === "followup_slot_selected") {
      setFollowUpSelectedSlotId(payload?.slot_id ?? null);
      return true;
    }
    if (event === "followup_booking_confirmed") {
      setFollowUpConfirmedBookingId(payload?.booking_id ?? null);
      return true;
    }
    if (event === "followup_pet_changed") {
      setFollowUpActivePetId(payload?.pet_id ?? null);
      if (payload?.cm_booking_recommendations || payload?.recommendations) {
        setFollowUpRecommendations(
          payload.cm_booking_recommendations ??
          payload.recommendations ??
          null,
        );
      }
      return true;
    }
    if (event === "followup_scroll_step") {
      const direction = payload?.direction === "up" ? "up" : "down";
      const delta = Number(payload?.delta ?? (direction === "up" ? -240 : 240));
      setFollowUpScrollCommand({ direction, delta, ts: Date.now() });
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (cartId) {
      setShouldShowReminders(false);
      setBillingSummary(null);
      setShowMirrorFollowUp(false);
    }
  }, [cartId])

  const isZeroPayableSuccess = !showMirrorFollowUp && !paymentCommand && billingSummary?.total_payable <= 0;

  useEffect(() => {
    if (!isZeroPayableSuccess || isEmbedded) return;
    const timer = setTimeout(() => {
      sendMirrorEvent("REMINDERS", {
        vms_user_id: userLogInfo,
        customer_id: billingSummaryCustomerId ?? billingData?.customer_id,
      });
      logVMSClick(VMS_CLICK_EVENTS.REMINDERS_SENT, "Reminders", {
        event_action: "ZERO_PAYABLE",
        cart_id: cartId,
        client_id: billingSummaryCustomerId ?? billingData?.customer_id,
      });
    }, 5000);
    return () => clearTimeout(timer);
  }, [isZeroPayableSuccess]);

  const prevShowMirrorFollowUpRef = useRef<boolean>(false);

  useEffect(() => {
    if (prevShowMirrorFollowUpRef.current === showMirrorFollowUp) return;
    prevShowMirrorFollowUpRef.current = showMirrorFollowUp;
    const logBody: IClickLogBody = {
      event_type: showMirrorFollowUp ? "FOLLOWUP_MIRROR_OPENED" : "FOLLOWUP_MIRROR_CLOSED",
      client_id: followUpCustomerId ?? undefined,
      vms_user_id: followUpVmsUserId ?? userLogInfo,
      pet_id: followUpActivePetId ?? undefined,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      scr_id: "billing_mirror",
      timestamp: Date.now(),
    };
    logAPI(logBody);
  }, [showMirrorFollowUp])

  return (
    <div className={isEmbedded ? appliedClass : `fixed inset-0 z-50 flex flex-col bg-white select-none overflow-hidden h-screen w-screen ${appliedClass}`}>
      {showMirrorFollowUp ? (
        <MirrorFollowUpComponent
          customerId={followUpCustomerId}
          vmsUserId={followUpVmsUserId}
          recommendations={followUpRecommendations}
          slotsFilter={followUpSlotsFilter}
          selectedSlotId={followUpSelectedSlotId}
          confirmedBookingId={followUpConfirmedBookingId}
          activePetId={followUpActivePetId}
          clinicName={mappedClinicName}
          scrollCommand={followUpScrollCommand}
        />
      )
        : paymentCommand || billingSummary ? (
          <BillingSummaryComponent
            summary={billingSummary}
            mappedClinicName={mappedClinicName}
            payment={paymentCommand}
            onPaymentEnd={() => {
              if (!isEmbedded) {
                logVMSClick(VMS_CLICK_EVENTS.MIRROR_QR_EXPIRED, "QR Expired", {
                  cart_id: cartId,
                  order_id: paymentCommand?.orderId,
                  amount: paymentCommand?.amount,
                });
              }
              handlePaymentMessage({ event_type: "payment_qr" });
            }}
          />
        )
        : cartId ? (
          <BillingViewComponent
            cartID={cartId}
            mirrorCustomerId={billingData?.customer_id}
            emrCommand={resolvedEmrCommand}
            membershipCommand={membershipCommand}
            pitchingCommand={pitchingCommand}
            source={source}
          />
        )
          : shouldShowReminders ? (
            <ReminderComponent
              mirrorCustomerId={billingData?.customer_id ?? null}
              mirrorCartId={billingData?.last_cart_id ?? null}
              mappedClinicName={mappedClinicName}
            />
          )
            : (
              <VeticScreenSaver />
            )}
    </div>
  );
};

export default BillingMirrorComponent;
